import React, { useState, lazy, Suspense } from 'react';
import { 
  Button, 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import { History as HistoryIcon, Close as CloseIcon } from '@mui/icons-material';
import { auditLogService, AuditLog } from '../../services/auditLogService';

// Lazy load EntityHistory component to avoid circular dependencies
const EntityHistory = lazy(() => import('./EntityHistory'));

interface EntityHistoryButtonProps {
  entityType: string;
  entityId: string;
  buttonLabel?: string;
  showIcon?: boolean;
  canRestore?: boolean;
  withoutConfirmation?: boolean;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'inherit';
  onVersionRestored?: () => void;
}

const EntityHistoryButton: React.FC<EntityHistoryButtonProps> = ({ 
  entityType, 
  entityId,
  buttonLabel = "تاریخچه تغییرات",
  showIcon = true,
  canRestore = false,
  withoutConfirmation = false,
  variant = 'outlined',
  size = 'small',
  color = 'info',
  onVersionRestored
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleRestoreVersion = async (version: AuditLog) => {
    if (!version._id) {
      setSnackbar({
        open: true,
        message: 'شناسه نسخه نامعتبر است',
        severity: 'error'
      });
      return;
    }
    
    setRestoreLoading(true);
    try {
      await auditLogService.restoreEntityToState(
        entityType,
        entityId,
        version._id
      );
      
      setSnackbar({
        open: true,
        message: 'نسخه با موفقیت بازیابی شد',
        severity: 'success'
      });
      
      // Close dialog after successful restoration
      setDialogOpen(false);
      
      // Call callback if provided
      if (onVersionRestored) {
        onVersionRestored();
      }
    } catch (error) {
      console.error('Error restoring version:', error);
      
      setSnackbar({
        open: true,
        message: 'خطا در بازیابی نسخه',
        severity: 'error'
      });
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <>
      {/* Button to open history dialog */}
      <Button
        variant={variant}
        size={size}
        color={color}
        onClick={handleOpenDialog}
        startIcon={showIcon ? <HistoryIcon /> : undefined}
      >
        {buttonLabel}
      </Button>

      {/* Dialog containing the EntityHistory component */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          تاریخچه تغییرات
          <Tooltip title="بستن">
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          <Suspense fallback={<CircularProgress />}>
            <EntityHistory 
              entityType={entityType} 
              entityId={entityId} 
              canRestore={canRestore}
              withoutConfirmation={withoutConfirmation}
              onRestoreVersion={canRestore ? handleRestoreVersion : undefined}
            />
          </Suspense>
        </DialogContent>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EntityHistoryButton; 